import { useState, useEffect, useMemo } from "react";
import { Typography, Menu, Modal, Descriptions, Checkbox } from "antd";
import { WarningOutlined } from "@ant-design/icons";
import {DataStore} from '@reams/elias-store';
import { Link, useParams, useNavigate } from "react-router-dom";
import { Space, Asset } from "models";
import { Helmet } from "react-helmet";
import {
  customFacetList,
  spaceDefinition,
} from "helpers/drawerData";
import ItemDrawer from "components/ItemDrawer/ItemDrawer";
import FilterHeader from "components/FilterHeader/FilterHeader";
import {
  AssetIcon,
  StarIcon,
  DeleteIcon,
  StarFilledIcon,
  EditIcon,
} from "components/Icons";
import { renderUpdatedAt } from "helpers/generalHelpers";
import { Status } from "helpers/statusHelpers";
import TableColumnTitle from "components/TableColumnTitle/TableColumnTitle";
import Spinner from "components/Spinner/Spinner";
import ItemTableGrid from "components/ItemTableGrid/ItemTableGrid";
import { saveFavouriteSpaces } from "helpers/favouriteSpaceHelpers";
import { Floor } from "models";
import FabricGroups from "components/FabricGroups/FabricGroups";
import Authorize from "components/Authorize/Authorize";
import { HiddenActionsInDropdown } from "components/ButtonPair/ActionsDropdown";

import "../../styles/customLinks.scss";
import { buildFilterableTreeByFacet } from "helpers/filters/assetFilterHelpers";
import { fetchFilterDescription, filterItem } from "helpers/filters/itemFilterHelpers";
import { spaceAssets, floorFabricAssets } from "helpers/asset/assetHelpers";
import { getSessionStorage, setSessionStorage, getLocalStorage, setLocalStorage } from "helpers/browserStorage";
import { logUserEvent } from "helpers/userEventLogger";
import { useOnlineContext } from "helpers/contexts/OnlineContext/OnlineContext";
import { useEntityData } from "EntityDataContext/EntityDataContext";

export default function FloorDetailsPage({
  pageHeader,
  addPageToHistory,
}) {
  const PAGE_KEY = "SpacesListing";
  const DEFAULT_SORT = "updatedAt";
  const DEFAULT_SORT_ORDER = "descend";

  const navigate = useNavigate();

  const {
    favouriteSpacesState,
    facilities,
    assets,
    spaces,
    floors,
    deletedSpaces,
    setDeletedSpaces,
    userData: { email },
    syncStatusHook: { imagesToUploadCount }
  } = useEntityData();

  const { isOnline } = useOnlineContext();

  const { floorId } = useParams();
  const floor = floors.items?.[floorId]

  const [sortBy, setSortBy] = useState(getLocalStorage(PAGE_KEY, "SortBy") || DEFAULT_SORT);
  const [sortOrder, setSortOrder] = useState(getLocalStorage(PAGE_KEY, "SortOrder") || DEFAULT_SORT_ORDER);
  const [favouriteSpaces, setFavouriteSpaces] = favouriteSpacesState;
  const [isCreatingNewSpace, setIsCreatingNewSpace] = useState(false);
  const [selectedFloor, setSelectedFloor] = useState(false);
  const [selectedSpace, setSelectedSpace] = useState();
  const [filters, setFilters] = useState([]);

  useEffect(() => {
    const currentSessionFilters = getSessionStorage(PAGE_KEY, "Filters") || [];

    if (currentSessionFilters.length && !filters.length) {
      currentSessionFilters.map((f) => setFiltersNode(f));
    }
     // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const sessionSortBy = getLocalStorage(PAGE_KEY, "SortBy");
    const sessionSortOrder = getLocalStorage(PAGE_KEY, "SortOrder");

    if (sortBy !== sessionSortBy) {
      setLocalStorage(PAGE_KEY, "SortBy", sortBy);
    }

    if (sortOrder !== sessionSortOrder) {
      setLocalStorage(PAGE_KEY, "SortOrder", sortOrder);
    }
  }, [sortBy, sortOrder])
  
  const facility = useMemo(() => {
    if (!floor) return undefined

    const facility = facilities?.[floor.facilityId]
    return facility
  }, [floor, facilities])

  const setFiltersNode = (val) => {
    let desc = val;
    desc = fetchFilterDescription({filter: desc, facets: facets});
    const x = { id: val, description: desc };

    setFilters((old) => {
      if (old.find((o) => o.id === x.id)) {
        return [...old];
      }
      return [...old, x];
    });
  };

  const onFiltersChange = (node) => {
    setSessionStorage(PAGE_KEY, "Filters", node);

    // from select fields
    if (Array.isArray(node)) {
      node.map((node) => setFiltersNode(node));
      return;
    }

    // from option fields
    setFiltersNode(node.value);
  };

  const onFilterClose = (filter) => {
    const newFilterKeys = (getSessionStorage(PAGE_KEY, "Filters") || []).filter(f => f !== filter);

    setSessionStorage(PAGE_KEY, "Filters", newFilterKeys);

    setFilters((old) => {
      return old.filter((f) => f.id !== filter);
    });
  };

  const facets = useMemo(() => {
    return customFacetList
    .filter((facet) => spaceDefinition.facets?.includes(facet.facetId))
  }, [customFacetList, spaceDefinition])
  
  const floorSpaces = spaces.groupByFloor[floor?.id]?.map((spaceId) => spaces.items[spaceId]) || []
  
  const enhancedFloorSpaces = useMemo(() => {
    let tmpspaces = floorSpaces
    .map((space) => {
      return {
        ...space,
        assetCount: spaceAssets({
          assets, 
          space
        }).length,
      };
    })
    .filter((space) => {
      return !deletedSpaces.find(id => id === space.id)
    });

    return filterItem(tmpspaces, filters, facets)
  }, [filters, deletedSpaces, assets, floorSpaces, facets])

  const fabricAssets = floorFabricAssets(assets, floor)

  useEffect(() => {
    if (!floor) {
      return;
    }

    addPageToHistory(floor.name);
  }, [floor]);

  const actions = (space) => {
    return [
      {
        label: "Assets",
        key: "assets",
        link: `/space/${space.id}`,
        icon: <AssetIcon />,
      },
      {
        label: "Edit",
        key: "edit",
        onClick: () => {
          const { id } = space;

          logUserEvent({
            user: email,
            eventType: `Edit space ${id} clicked on spaces listing via Floor Details view`,
            imageOutboxSize: imagesToUploadCount
          }, isOnline);

          setSelectedSpace(space);
        },
        icon: <EditIcon />,
      },
      {
        label: "Favourite",
        key: "favourite",
        onClick: () => {
          const { id } = space;

          logUserEvent({
            user: email,
            eventType: `Space ${id} added/removed from favourite spaces on spaces listing via Floor Details view`,
            imageOutboxSize: imagesToUploadCount
          }, isOnline);

          saveFavouriteSpaces(setFavouriteSpaces, favouriteSpaces, space.id);
        },
        icon: favouriteSpaces.includes(space.id) ? (
          <StarFilledIcon />
        ) : (
          <StarIcon />
        ),
      },
      {
        label: "Delete",
        key: "delete",
        onClick: () => confirmDeleteSpace(space),
        icon: <DeleteIcon />,
      },
    ];
  };

  const onConfirmDelete = (checked, modal) => {
    modal.update((prevConfig) => ({
      ...prevConfig,
      okButtonProps: {
        disabled: !checked
      }
    }));
  }

  async function confirmDeleteSpace(space) {
    const modal = Modal.confirm({
      className: "delete-confirmation-modal",
      title: `Delete Space - ${space.name}`,
      content:  
        <>
          <p>
            Are you sure you want to delete <span className="delete-confirmation-assertive-text">{space.name}</span>?<br/>
            {space.assetCount === 1 ? 'Then ' : 'All '}
            <span className="delete-confirmation-assertive-text">{space.assetCount} asset{space.assetCount !== 1 ? 's' : null}</span> belonging to this space will also be deleted.
          </p>
          <Checkbox
            className="delete-confirmation-checkbox"
            onChange={(event) => onConfirmDelete(event.target.checked, modal)}
          >
            <span className="delete-confirmation-assertive-text">Yes, remove space and assets</span>
          </Checkbox>
        </>,
      icon: <WarningOutlined className="delete-confirmation-warning-icon" />,
      okText: "Confirm",
      cancelText: "Cancel",
      width: 600,
      okButtonProps: {
        disabled: true
      },
      onOk: async () => {
        // Delete Assets in Space before deleting Space
        const assetsToDelete = await DataStore.query(Asset, (x) =>
          x.spaceId("eq", space.id)
        );

        for (let i = 0; i < assetsToDelete.length; i++) {
          await DataStore.delete(assetsToDelete[i]);
          
          console.info(`Deleted asset: ${assetsToDelete[i.name]}`);
        }

        const spaceToDelete = await DataStore.query(Space, space.id);
        
        await DataStore.delete(spaceToDelete);

        setDeletedSpaces((deleted) => {
          return [...deleted, spaceToDelete.id]
        });

        logUserEvent({
          user: email,
          eventType: `Space deleted ${space.id} via Delete Space modal on Floor Details view`,
          imageOutboxSize: imagesToUploadCount
        }, isOnline);
      },
    });
  }

  async function confirmDeleteFloor(floor) {
    const assetCount = Object.values(assets?.items)?.filter(a => a.floorId === floorId)?.length || null
    const spaceCount = Object.values(spaces?.items)?.filter(s => s.floorId === floorId)?.length || null

    const modal = Modal.confirm({
      className: "delete-confirmation-modal",
      title: `Delete Floor - ${floor.name}`,
      content:
        <>
          <p>
            Are you sure you want to delete <span className="delete-confirmation-assertive-text">{floor.name}</span>?<br/>
            {spaceCount === 1 ? 'Then ' : 'All '}
            <span className="delete-confirmation-assertive-text">{spaceCount} space{spaceCount !== 1 ? 's' : null}</span> and{' '}
            <span className="delete-confirmation-assertive-text">{assetCount} asset{assetCount !== 1 ? 's' : null}</span> belonging to this floor will also be deleted.
          </p>
          <Checkbox
            className="delete-confirmation-checkbox"
            onChange={(event) => onConfirmDelete(event.target.checked, modal)}
          >
            <span className="delete-confirmation-assertive-text">Yes, remove floor, spaces and assets</span>
          </Checkbox>
        </>,
      icon: <WarningOutlined className="delete-confirmation-warning-icon"/>,
      okText: "Confirm",
      cancelText: "Cancel",
      width: 600,
      okButtonProps: {
        disabled: true
      },
      onOk: async () => {
        // Delete Assets, Spaces on floor before deleting Floor
        const assetsToDelete = await DataStore.query(Asset, (x) =>
          x.floorId("eq", floor.id)
        );

        for (let i = 0; i < assetsToDelete.length; i++) {
          await DataStore.delete(assetsToDelete[i]);
          
          console.info(`Deleted asset: ${assetsToDelete[i].name}`);
        }

        const spacesToDelete = await DataStore.query(Space, (x) =>
          x.floorId("eq", floor.id)
        );
        
        for (let i = 0; i < spacesToDelete.length; i++) {
          await DataStore.delete(spacesToDelete[i]);

          console.info(`Deleted space: ${spacesToDelete[i].name}`);
        }

        const floorToDelete = await DataStore.query(Floor, floor.id);

        await DataStore.delete(floorToDelete);

        logUserEvent({
          user: email,
          eventType: `Floor deleted ${floor.id} via Delete Floor modal on Floor Details view`,
          imageOutboxSize: imagesToUploadCount
        }, isOnline);

        navigate(`/facility/${floor.facilityId}`);
      },
    });
  }

  const columnTitles = {
    localName: "Local name",
    assetCount: "Assets",
    gia: 'GIA',
    updatedAt: "Last Edited",
  };

  const tableColumnTitleProps = {
    sortBy,
    setSortBy,
    sortOrder,
    setSortOrder,
    columnTitles,
  };

  const SPACE_COLUMNS = [
    {
      title: <TableColumnTitle {...tableColumnTitleProps} dataIndex="name" />,
      dataIndex: "name",
      key: "Name",
      fixed: "left",
      render: (name, space) => {
        const externalId = space?.externalId || null
        
        return (
          <>
            <Link
              className="table-name-link"
              to={`/space/${space.id}`}
            >
              {name}
            </Link>
            {externalId && (
              <div>{externalId}</div>
            )}
          </>
      )},
      sorter: (a, b) =>
        a.name?.toLowerCase() < b.name?.toLowerCase() ? -1 : 1,
      sortOrder: sortBy === "name" ? sortOrder : false,
    },
    {
      title: <TableColumnTitle {...tableColumnTitleProps} dataIndex="status" />,
      dataIndex: "status",
      key: "Status",
      render: (status) => <Status status={status} />,
      sorter: (a, b) => (a.status < b.status ? -1 : 1),
      sortOrder: sortBy === "status" ? sortOrder : false,
    },
    {
      title: <TableColumnTitle {...tableColumnTitleProps} dataIndex="type" />,
      dataIndex: "type",
      key: "Type",
      sorter: (a, b) => (a.type < b.type ? -1 : 1),
      sortOrder: sortBy === "type" ? sortOrder : false,
    },
    {
      title: (
        <TableColumnTitle {...tableColumnTitleProps} dataIndex="assetCount" />
      ),
      key: "Assets",
      dataIndex: "assetCount",
      sorter: (a, b) => (a.assetCount < b.assetCount ? -1 : 1),
      sortOrder: sortBy === "assetCount" ? sortOrder : false,
    },
    {
      title: (
        <TableColumnTitle {...tableColumnTitleProps} dataIndex="gia" />
      ),
      key: "GIA",
      dataIndex: "gia",
      sorter: (a, b) => (a.gia < b.gia ? -1 : 1),
      sortOrder: sortBy === "gia" ? sortOrder : false,
      render: (gia, space) => space?.gia && (<div>{gia}m²</div>)
    },
    {
      title: (
        <TableColumnTitle {...tableColumnTitleProps} dataIndex="localName" />
      ),
      dataIndex: "localName",
      key: "Local name",
      sorter: (a, b) =>
        a?.localName?.toLowerCase() < b?.localName?.toLowerCase() ? -1 : 1,
      sortOrder: sortBy === "localName" ? sortOrder : false,
    },
    {
      title: (
        <TableColumnTitle {...tableColumnTitleProps} dataIndex="department" />
      ),
      dataIndex: "department",
      key: "Department",
      sorter: (a, b) =>
        a.department?.toLowerCase() < b.department?.toLowerCase() ? -1 : 1,
      sortOrder: sortBy === "department" ? sortOrder : false,
    },
    {
      title: (
        <TableColumnTitle {...tableColumnTitleProps} dataIndex="updatedAt" />
      ),
      key: "Last Edited",
      dataIndex: "updatedAt",
      render: (_, item) => {
        return renderUpdatedAt(item);
      },
      sorter: (a, b) => (a.updatedAt < b.updatedAt ? -1 : 1),
      sortOrder: sortBy === "updatedAt" ? sortOrder : false,
    },
    {
      title: "",
      fixed: "right",
      render: (space) => {
        return (
          <div className="row-actions">
            <HiddenActionsInDropdown actions={actions(space)} />
          </div>
        );
      },
    },
  ];

  const filterTreeData = useMemo(() => {
    // search through facets.json and find appropriate facets
    const filterableFacets = facets.filter((i) => i.filterable);
    return filterableFacets.map((i) => {
      return buildFilterableTreeByFacet(floorSpaces, i, onFiltersChange);
    })
  }, [spaces]);

  if (!facility || !floor) {
    return <Spinner message="We are fetching and processing your data..." />;
  }

  const render = (space) => (
    <Descriptions
      column={{ xxl: 1, xl: 1, lg: 1, md: 1, sm: 1, xs: 1 }}
      size={"small"}
    >
      <Descriptions.Item label="Status">
        <Status status={space.status} />
      </Descriptions.Item>
      <Descriptions.Item label="Space Type">{space.type}</Descriptions.Item>
      <Descriptions.Item label="Assets">{space.assetCount}</Descriptions.Item>
      <Descriptions.Item label="Last Edited">
        {renderUpdatedAt(space)}
      </Descriptions.Item>
    </Descriptions>
  );

  return (
    <div className="floor-details-page">
      <Helmet title={floor?.name || "Floor details"} />
      {pageHeader}
      <div className="content-header">
        <Typography.Title className="subtitle" level={1}>
          Floor details - {floor?.name}
        </Typography.Title>
        <FilterHeader
          filterTreeData={filterTreeData}
          filterOnChange={(change) => {
            onFiltersChange(change);
          }}
          filterOnCloseTag={(closeTag) => onFilterClose(closeTag)}
          filterValue={filters}
          buttonPair={{
            label: "Add Space",
            onClick: () => {
              logUserEvent({
                user: email,
                eventType: "Add Space button clicked on Floor Details view",
                imageOutboxSize: imagesToUploadCount
              }, isOnline)

              setIsCreatingNewSpace(true)
            },
            options: (
              <Menu>
                <Menu.Item
                  key="edit-floor"
                  onClick={() => {
                    logUserEvent({
                      user: email,
                      eventType: "Edit floor option clicked on Add Space button via Floor Details view",
                      imageOutboxSize: imagesToUploadCount
                    }, isOnline)

                    setSelectedFloor(true)
                  }}
                >
                  Edit floor
                </Menu.Item>
                <Menu.Item key="complete-floor">Complete floor</Menu.Item>
                <Menu.Item
                  key="delete-floor"
                  onClick={() => confirmDeleteFloor(floor)}
                >
                  Delete floor
                </Menu.Item>
              </Menu>
            ),
          }}
          sortByOptions={SPACE_COLUMNS}
          {...tableColumnTitleProps}
        />
      </div>
      <Authorize allowedRoles={["fabric"]}>
        <FabricGroups
          parentType="floor"
          parents={{ facility, floor }}
          fabricAssets={fabricAssets}
        />
      </Authorize>
      <ItemTableGrid
        title="Spaces"
        items={enhancedFloorSpaces}
        columns={SPACE_COLUMNS}
        onClick={(a) => setSelectedSpace(a)}
        actions={actions}
        render={render}
      />

      {(selectedSpace || isCreatingNewSpace) && (
        <ItemDrawer
          onClose={() => {
            setIsCreatingNewSpace(false);
            setSelectedSpace(false);
          }}
          visible={selectedSpace || isCreatingNewSpace}
          item={selectedSpace}
          parents={{ floor, facility }}
          itemType="space"
        />
      )}
      {floor && selectedFloor && (
        <ItemDrawer
          onClose={() => setSelectedFloor(false)}
          visible={floor}
          item={floor}
          parents={{ facility }}
          itemType="floor"
        />
      )}
    </div>
  );
}
